import { store } from '../store/stencil-redux';
import { appAccessDenied, appInstallAppSuccess, appSync, appSyncSuccess, appTrialExpired, loadApp, reloadApp } from '../store/app/app.actions';

import EventEmitter from "eventemitter3";
import { CoreoApiAppSummary, CreateProjectOptions, Unthunk } from '../types';
import { CoreoAPI } from './api.service';
import { imageToImageProxy } from './image.service';

export const projectCreationEvents = new EventEmitter<{
  start: () => void;
  progress: (step: number) => void;
  complete: (projectId: number) => void;
  error: (error: any) => void;
}>();

export interface ManageAppsProject {
  id: number;
  name: string;
  icon?: string;
  description: string;
  organisationFolderId: string;
}

export interface ManageAppsFolder {
  id: string;
  name: string;
  parentId: string;
}

export interface ManageAppsOrganisation {
  id: number;
  name: string;
  ukhabSeats?: number;
}

export interface ManageAppsOrganisationDetail extends ManageAppsOrganisation {
  folders: ManageAppsFolder[];
  projects: ManageAppsProject[];
}

export interface ManageAppsTreeNode extends ManageAppsFolder {
  children: ManageAppsTreeNode[];
  projects: ManageAppsProject[];
}

export interface ManageAppsOrganisationTree extends ManageAppsOrganisationDetail {
  children: ManageAppsTreeNode[];
}

interface OrganisationListResponse {
  organisations: {
    id: number;
    name: string;
    icon: string;
  }[];
}

interface ProjectSummaryResponse {
  project: {
    name: string;
    icon: string;
    description: string;
    allowContributors: boolean;
  }
}

export interface OrganisationUser {
  id: number;
  displayName: string;
  username: string;
}

interface OrganisationMembershipsResponse {
  organisation: {
    memberships: {
      user: OrganisationUser;
    }[];
  }
}

interface CoreoProjectTemplate {
  id: number;
  projectId: number;
  name: string | null;
  description: string | null;
  category: string | null;
  icon: string | null;
  banner: string | null;
  organisationTierId: number | null;
  organisationTierTooltip: string | null;
}

interface OrganisationProjectTemplateResponse {
  id: number;
  projectId: number;
  name: string;
  description: string | null;
  updatedAt: string;
  master: boolean;
  icon: string | null;
}

export interface OrganisationProjectTemplate {
  id: number;
  projectId: number;
  name: string;
  description: string | null;
  icon: string | null;
  master: boolean;
}

export class ProjectService {
  public static _instance: ProjectService;

  appSync: Unthunk<typeof appSync>;
  appSyncSuccess: Unthunk<typeof appSyncSuccess>;
  appInstallAppSuccess: Unthunk<typeof appInstallAppSuccess>;
  appAccessDenied: Unthunk<typeof appAccessDenied>;
  appTrialExpired: Unthunk<typeof appTrialExpired>;
  loadApp: Unthunk<typeof loadApp>;
  reloadApp: Unthunk<typeof reloadApp>;

  static get instance(): ProjectService {
    if (!ProjectService._instance) {
      ProjectService._instance = new ProjectService();
    }
    return ProjectService._instance;
  }

  private constructor() {
    store.mapDispatchToProps(this, {
      appSync,
      appSyncSuccess,
      appInstallAppSuccess,
      appAccessDenied,
      appTrialExpired,
      loadApp,
      reloadApp
    });
  }

  createProject = async (options: CreateProjectOptions): Promise<{ projectId: number }> => {
    projectCreationEvents.emit('start');
    projectCreationEvents.emit('progress', 0);
    const mutation = `
      mutation CreateProject($input: ProjectInput!) {
        project: createProject(input: $input) {
          id
        }
      }
    `;

    const result = await CoreoAPI.instance.graphql<{project: { id: number }}>(mutation, { input: options });

    if (result.errors?.length) {
      projectCreationEvents.emit('error', result.errors[0].message);
      throw new Error(result.errors[0].message);
    }

    projectCreationEvents.emit('progress', 1);
    projectCreationEvents.emit('complete', result.data.project.id);
    return { projectId: result.data.project.id };
  };

  cloneProject = async (options: CreateProjectOptions): Promise<{ projectId: number }> => {
    projectCreationEvents.emit('start');
    projectCreationEvents.emit('progress', 0);
    const mutation = `
      mutation CreateProjectFromTemplate($input: CloneProjectTemplateInput!) {
        project: createProjectFromTemplate(input: $input) {
          id
        }
      }
    `;

    const input = {
      projectId: options.templateId,
      organisationId: options.organisationId,
      name: options.name,
      description: options.description,
      memberships: options.memberships
    };

    const result = await CoreoAPI.instance.graphql<{project: { id: number }}>(mutation, { input });

    if (result.errors?.length) {
      projectCreationEvents.emit('error', result.errors[0].message);
      throw new Error(result.errors[0].message);
    }

    projectCreationEvents.emit('progress', 1);
    projectCreationEvents.emit('complete', result.data.project.id);
    return { projectId: result.data.project.id };
  };

  getAppsList = async (): Promise<CoreoApiAppSummary[]> => {
    const projectList = `
      query ProjectList {
        projects(order: "name", where: { bespoke: false }) {
          name,
          icon: imageUrl,
          id,
          description,
          allowContributors
        }
      }
    `;

    const result = await CoreoAPI.instance.graphql<{ projects: CoreoApiAppSummary[] }>(projectList, {});

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    for (const app of result.data.projects) {
      app.icon = imageToImageProxy(app.icon);
    }
    return result.data.projects;
  }

  getAppSummaryFromAPI = async (id: number): Promise<CoreoApiAppSummary> => {
    const projectSummary = `
      query ProjectSummary($projectId: Int!) {
        project: projectOverview(id: $projectId) {
          name
          icon: imageUrl
          description
          allowContributors
        }
      }
    `;

    const result = await CoreoAPI.instance.graphql<ProjectSummaryResponse>(projectSummary, { projectId: id });

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    if (!result.data.project) {
      throw new Error('Project not found');
    }

    const { name, icon, description, allowContributors } = result.data?.project;

    return {
      id,
      name,
      icon: imageToImageProxy(icon),
      description,
      allowContributors
    };
  }

  getProjectTemplates = async (): Promise<CoreoProjectTemplate[]> => {
    const response = await CoreoAPI.instance.get('/aa/templates');
    if (!response.ok) {
      throw new Error(`Failed to load project templates (${response.status})`);
    }
    return response.json();
  };

  getOrganisationProjectTemplates = async (organisationId: number): Promise<OrganisationProjectTemplate[]> => {
    try {
      const response = await CoreoAPI.instance.get(`/organisations/${organisationId}/templates`);
      if (!response.ok) {
        throw new Error(`Failed to load organisation templates (${response.status})`);
      }
      const templates: OrganisationProjectTemplateResponse[] = await response.json();
      return templates.map((template) => ({
        id: template.id,
        projectId: template.projectId,
        name: template.name ?? '',
        description: template.description ?? null,
        icon: template.icon ?? null,
        master: template.master,
      }));
    } catch (error) {
      console.warn('Failed to load organisation templates', error);
      return [];
    }
  };

  joinProject = async (projectId: number): Promise<string> => {
    const joinProjectMutation = `
      mutation JoinProject($projectId: Int!){
        joinProject(input:{ projectId: $projectId } ){
          role
        }
      }
    `;

    const response = await CoreoAPI.instance.graphql<{ joinProject: { role: string } }>(joinProjectMutation, {
      projectId
    });
    if (response?.errors?.length > 0) {
      throw response.errors[0];
    }

    const result = response?.data?.joinProject?.role;
    return result;
  }

  getOrganisationList = async (): Promise<{ id: number; name: string }[]> => {

    const organisationList = `
      query OrganisationList {
        organisations {
          id
          name
          icon: imageUrl
        }
      }
    `;

    const result = await CoreoAPI.instance.graphql<OrganisationListResponse>(organisationList, {});

    if (result.errors) {
      throw new Error(result.errors[0].message);
    }

    const organisations = result.data.organisations;
    for (const org of organisations) {
      org.icon = imageToImageProxy(org.icon);
    }
    organisations.sort((a, b) => a.name.localeCompare(b.name));
    return organisations;
  }

  getOrganisationMemberships = async (id: number): Promise<OrganisationUser[]> => {
    const query = `query AAGetOrganisationrojectMemberships($organisationId: Int!) {
        organisation(id: $organisationId){
            memberships{
                user{
                    id,
                    displayName
                    username
                }
            }
        }
    }`;
    const result = await CoreoAPI.instance.graphql<OrganisationMembershipsResponse>(query, { organisationId: id });
    let memberships = result.data.organisation.memberships || [];
    return memberships.filter(m => !!m.user).map(m => m.user);
  }

  getOrganisationDetail = async (id: number): Promise<ManageAppsOrganisationDetail> => {
    const organisationDetail = `
      query OrganistionDetail($id: Int!) {
        organisation(id: $id) {
          id,
          name,
          ukhabSeats,
          folders{
            id
            name
            parentId
          }
          projects{
              id
              name
              icon: imageUrl
              description
              organisationFolderId
          }
        }
      }
    `;

    const result = await CoreoAPI.instance.graphql<{ organisation: ManageAppsOrganisationDetail }>(organisationDetail, { id });
    const org = result.data.organisation;
    org.projects.sort((a, b) => a.name.localeCompare(b.name));
    for (const project of org.projects) {
      project.icon = imageToImageProxy(project.icon);
    }
    return org;
  }

  getContributorProjects = async (): Promise<ManageAppsTreeNode | null> => {
    const query = `query AppGetContributorProjects {
      viewer {
        projects: contributorProjects {
              id
              name
              icon: imageUrl
              description
              organisationFolderId
        }
      }
    }`;
    const result = await CoreoAPI.instance.graphql<{ viewer: { projects: CoreoApiAppSummary[] } }>(query, {});
    if (result.data.viewer.projects.length === 0) {
      return null;
    }

    result.data.viewer.projects.sort((a, b) => a.name.localeCompare(b.name));
    return {
      id: null,
      name: 'Contributor Projects',
      parentId: null,
      children: [],
      projects: result.data.viewer.projects.map(p => ({
        ...p,
        organisationFolderId: null,
        icon: imageToImageProxy(p.icon)
      }))
    };
  }

  public buildOrganisationTree(organisation: ManageAppsOrganisationDetail): ManageAppsTreeNode {
    return this.buildTree(organisation.projects, organisation.folders);
  }

  public buildFlatOrganisationStructure(organisation: ManageAppsOrganisationDetail) {
    const tree = this.buildTree(organisation.projects, organisation.folders);
    const flatten = (item) => item.children?.flatMap(child => [child, ...flatten(child)]) || [];

    return [...flatten(tree), ...organisation.projects];
  }

  private buildTree(projects: ManageAppsProject[], folders: ManageAppsFolder[], folderId: string | null = null): ManageAppsTreeNode {
    const filteredProjects = projects.filter(p => p.organisationFolderId === folderId);
    const children = folders.filter(f => f.parentId === folderId).map(f => this.buildTree(projects, folders, f.id));
    const folder: ManageAppsFolder = folders.find(f => f.id === folderId) ?? { id: null, name: '', parentId: null };
    return { ...folder, projects: filteredProjects, children }
  };

  findTree(tree: ManageAppsTreeNode, id: string): ManageAppsTreeNode {
    if (tree.id === id) {
      return tree;
    }
    if (tree.children) {
      for (let i = 0; i < tree.children.length; i++) {
        const found = this.findTree(tree.children[i], id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }
}
